/* GENERATED from src/js/ndog-free-gift.js — edit the source, then run `make js`. Never edit this file by hand. */
/*
  ndog-free-gift.js — Free-gift module (<ndog-free-gift>) for the Cart Drawer v2
  Feature free-gift, 2026-08-18. Rendered inside <ndog-cart-drawer> by
  snippets/ndog-cart-drawer.liquid (only when the theme setting is enabled)
  with a JSON config built from the theme settings.

  How it works
  - after every cart change (event ndog:cart:updated from the drawer) it plans
    which 0-€ gift lines the cart SHOULD contain (planGifts, pure function),
    compares with what IS there (diffGifts) and fixes the difference through
    the drawer's mini-API (updateLines / addItems) — never more than one
    update + one add call, followed by one control round that must be a no-op
  - gift lines are recognised by the line item property `_ndog_gift=<rule>`
    rules: guetzli · brochure · card · abo_bag · choice
  - every gift carries four size sides ({split, a, b, c, d} — each
    {variants} or null): the largest can decides — a = 100 g (or no can at
    all; and the only side while the gram split is off), b = 200 g,
    c = 400 g, d = 800 g. An EMPTY side means: no gift for that can size
    (no fallback). The choice slots in a MIXED-size cart offer the union of
    all filled, present groups instead (largest first = default) via their
    tile select. The guetzli rule ALWAYS offers every filled side (auto
    size = default, largest can) so the customer may pick Klein vs.
    Mittel-gross — since 2026-08-23 as SIZE TILES (data-gift-size, chef:
    the dropdown hid the choice) instead of the chip select. The Mechanic
    task reads the chosen gift line from the initial order and mirrors it
    into the Seal subscription, so the choice survives into every renewal
    (Pablo, 2026-08-21). The customer may override the picked variant via
    a <select> (data-gift-variant) or a size tile where one is rendered —
    the override is kept in sessionStorage per rule and wins while it
    exists in the applying union/side
  - tier 1 (cart value): guetzli · bundle products: brochure + card ·
    subscription line: gift bag once — its size side is read from the
    SUBSCRIBED cans only (the bag packs a lid: b/c = 200/400 g bag,
    d = 800 g bag, a = the rest), never from one-off cans in the cart
  - a bundle in the cart switches the value tiers OFF (chef, 2026-09-03):
    no guetzli, no choice — instead the bundle contents go in as 0-€ lines
    (rules bundle_baerli · bundle_treats · bundle_lid from config.bundleRules,
    variant ids fixed in code per store, size side = the bundle variant);
    the drawer body shows those lines instead of the tier timeline.
    With the setting "bundle contents display-only" there are NO such lines;
    instead the module stamps the contents text from the drawer markup
    (data-bundle-contents-map, built from the variant metafield) onto the
    bundle line as property `_ndog_bundle_contents` via the drawer's
    updateLineProperties — the
    order mail and the invoice print that property, because Shopify
    notifications cannot resolve reference metafields (2026-09-09)
    tier 2/3: the customer picks ONE gift SLOT via the tiles in the drawer
    body (data-gift-choice = slot key like "tier2_1"); the choice is stored
    in sessionStorage AND derived from the existing `choice` line via its
    variant id (the cart is the truth)
  - the drawer-upsell tile shares that ONE choice slot: it is a PAID line and
    carries `_ndog_upsell` (never `_ndog_gift`, or the reconciler would delete
    it as an unplanned gift). Since 2026-08-26 one tile per variant, and the
    line is hidden from the item list — the marked tile represents it, like a
    chosen gift. It still counts as cart value (Pablo, 2026-08-21). It only
    exists together with its tile: once the cart value drops below tier 2
    (or a bundle joins) the reconciler removes the line again, exactly like
    a chosen free gift (Pablo, 2026-09-04 — before, the paid line surfaced
    in the item list as soon as its row disappeared)
  - "cart value" = paid lines only, Seal % discount mirrored, in cents — the
    same number the Liquid body renders as data-paid-subtotal-cents
  - dismissed automatic gifts (setting "customer may remove") stay away for
    this browser session; a rule that fails to add is blocked for the session
  - every customer action (tile clicks, size tiles, selects) and every cart
    event runs through ONE queue (run/settle): a click while another action
    is still on its way to the server waits for that answer instead of
    reading the old cart (Pablo, 2026-09-04: ball picked, treats clicked
    right after → the treats never saw the ball line, both stayed in the
    cart). Only the clicked card blocks itself by CSS; the queue keeps the
    other cards in order
  Nothing here is global: everything lives inside the IIFE. The pure functions
  above the custom element are loaded by src/js/tests/ndog-free-gift-rules-test.js
  (`node`), so keep them free of DOM/window access.
*/
(() => {
  // "400g …", "18x200g", "6x800g" — but not "1200g", "40g" or "300g"
  const SIZE_PATTERN = /(?:^|[^0-9])(100|200|400|800)\s*g\b/gi;
  const STORAGE_KEY = 'ndog_gift_state';
  const BUNDLE_CONTENTS_PROPERTY = '_ndog_bundle_contents';
  // Pause between a cart change and the automatic gift sync. The customer's
  // own /cart/add.js has to stand alone for a moment: Shopify's add-to-cart
  // analytics is reported to drop the event when another cart request
  // follows right away (community reports, unanswered by Shopify; funnel gap
  // since the drawer go-live 2026-09-04). The pause is also the beat that
  // lets the appear animation read as "something new arrived" (2026-10-24).
  const GIFT_SYNC_DELAY_MS = 1500;
  // Elements the appear animation may mark after an automatic sync: the
  // secured box, a tile group, and everything bound to a gift key (chips,
  // gift lines, bundle pills, the chosen tile)
  const APPEAR_SELECTOR = '.ndog-cart-drawer__secured, .ndog-cart-drawer__gift-group, [data-gift-key]';

  // True for a line that this module created (carries the _ndog_gift property)
  function isGiftLine(item) {
    return !!(item && item.properties && item.properties._ndog_gift);
  }

  // True for the drawer-upsell line (paid, carries the _ndog_upsell property)
  function isUpsellLine(item) {
    return !!(item && item.properties && item.properties._ndog_upsell);
  }

  // True while one of the configured bundle products is among the paid lines
  function hasBundleLine(paid, config) {
    const bundleIds = ((config && config.bundleProductIds) || []).map(Number);
    return paid.some((item) => bundleIds.includes(Number(item.product_id)));
  }

  // Upsell lines that have to LEAVE the cart: the discounted treat exists
  // only together with its tile in the choice row (tier 2 reached, no
  // bundle) — below that the paid line would sit in the cart without the
  // tile that represents it (Pablo, 2026-09-04). Same gate as the Liquid
  // row (gift_tiles_due): the upsell itself counts as cart value, so a
  // removal can never flip the gate back on.
  function staleUpsellLines(cart, config) {
    const items = (cart && cart.items) || [];
    const upsell = items.filter(isUpsellLine);
    if (upsell.length === 0 || !config) return [];
    const paid = items.filter((item) => !isGiftLine(item));
    const eligible = !hasBundleLine(paid, config) && paidSubtotalCents(paid) >= config.tier2Cents;
    return eligible ? [] : upsell;
  }

  // True for a Seal subscription line (any persisted `_sealsub*` property)
  // Bundle lines that still lack the contents text as line property (display-only
  // mode only). texts = { lineKey: "Bärli, Leckerli Guetzlis, …" } from the drawer
  // markup. Returns /cart/change.js payloads; existing properties are kept, because
  // change.js replaces the whole property set.
  function bundleContentStamps(cart, config, texts) {
    if (!config.bundleDisplayOnly) return [];
    const bundleIds = (config.bundleProductIds || []).map(Number);
    return ((cart && cart.items) || [])
      .filter((item) => bundleIds.includes(Number(item.product_id)) && !isGiftLine(item))
      .filter((item) => texts[item.key] && !(item.properties && item.properties[BUNDLE_CONTENTS_PROPERTY]))
      .map((item) => ({
        id: item.key,
        quantity: item.quantity,
        properties: Object.assign({}, item.properties || {}, { [BUNDLE_CONTENTS_PROPERTY]: texts[item.key] }),
      }));
  }

  function isSubscriptionLine(item) {
    return Object.keys((item && item.properties) || {}).some((key) => key.indexOf('_sealsub') === 0);
  }

  // Seal subscription discount % from the persisted `_sealsub_discount_<pct>` property (0 if none)
  function sealDiscountPercent(item) {
    const keys = Object.keys((item && item.properties) || {});
    for (const key of keys) {
      if (key.indexOf('_sealsub_discount_') === 0) {
        const pct = parseInt(key.replace('_sealsub_discount_', ''), 10);
        return Number.isNaN(pct) ? 0 : pct;
      }
    }
    return 0;
  }

  // Cart value in cents: paid lines only, subscription discount mirrored (same formula as the Liquid body)
  function paidSubtotalCents(paidItems) {
    return paidItems.reduce((sum, item) => {
      const full = item.final_line_price != null ? item.final_line_price : item.line_price || 0;
      const pct = sealDiscountPercent(item);
      return sum + Math.round((full * (100 - pct)) / 100);
    }, 0);
  }

  // Set of can sizes (100/200/400/800) found in the paid lines' titles, e.g. "400g …" or "18x200g"
  function canSizes(paidItems) {
    const sizes = new Set();
    paidItems.forEach((item) => {
      const text = `${item.variant_title || ''} ${item.title || ''}`;
      let match;
      SIZE_PATTERN.lastIndex = 0;
      while ((match = SIZE_PATTERN.exec(text)) !== null) sizes.add(parseInt(match[1], 10));
    });
    return sizes;
  }

  // Variants of the size group that applies. The LARGEST can in the cart
  // decides — 800 g → d, 400 g → c, 200 g → b, 100 g (or no can at all)
  // → a; split off = always side a. An EMPTY side deliberately means: no
  // gift for that can size (no fallback).
  function sideVariantsFor(entry, sizes) {
    if (!entry) return [];
    let side = 'a';
    if (entry.split) {
      if (sizes.has(800)) side = 'd';
      else if (sizes.has(400)) side = 'c';
      else if (sizes.has(200)) side = 'b';
    }
    const chosen = entry[side];
    return (chosen && chosen.variants) || [];
  }

  // Variants offered for the guetzli rule: the automatic side (largest can)
  // leads and is the default; every OTHER filled side follows so the
  // customer may switch sizes via the chip select. An empty automatic side
  // still means: no guetzli for that can size (strict rule unchanged).
  function guetzliVariantsFor(entry, sizes) {
    if (!entry) return [];
    const auto = sideVariantsFor(entry, sizes);
    if (!auto.length || !entry.split) return auto;
    const offered = auto.slice();
    ['a', 'b', 'c', 'd'].forEach((side) => {
      const variants = (entry[side] && entry[side].variants) || [];
      variants.forEach((v) => {
        if (!offered.some((o) => Number(o.id) === Number(v.id))) offered.push(v);
      });
    });
    return offered;
  }

  // First variant of the list, unless the customer's override is (still) in it
  function pickVariant(variants, overrideId) {
    if (!variants.length) return null;
    const hit = overrideId ? variants.find((v) => Number(v.id) === Number(overrideId)) : null;
    return (hit || variants[0]).id;
  }

  // Size groups present in the cart, largest first ('a' also covers carts
  // without any can)
  function presentGroups(sizes) {
    const groups = [];
    if (sizes.has(800)) groups.push('d');
    if (sizes.has(400)) groups.push('c');
    if (sizes.has(200)) groups.push('b');
    if (sizes.has(100) || groups.length === 0) groups.push('a');
    return groups;
  }

  // The variant for the customer's chosen tier-2/3 slot, or null if not eligible/not chosen.
  // Split on: with ONE size group in the cart the system decides; a MIXED cart
  // offers the union of all filled, present groups (largest first = default)
  // and the customer picks via the tile select (Pablo, 2026-08-20).
  function choiceVariantId(config, choiceKey, paidCents, sizes, overrideId) {
    if (!choiceKey || paidCents < config.tier2Cents) return null;
    const choices = config.choices || {};
    const allowed = (choices.tier2 || []).concat(paidCents >= config.tier3Cents ? choices.tier3 || [] : []);
    const slot = allowed.find((entry) => entry.key === choiceKey);
    if (!slot) return null;
    if (!slot.split) {
      const variants = (slot.a && slot.a.variants) || [];
      return variants.length ? pickVariant(variants, overrideId) : null;
    }
    const groups = presentGroups(sizes).filter((side) => slot[side] && slot[side].variants && slot[side].variants.length > 0);
    if (groups.length === 0) return null;
    const union = [];
    groups.forEach((side) => {
      slot[side].variants.forEach((v) => {
        if (!union.some((u) => Number(u.id) === Number(v.id))) union.push(v);
      });
    });
    if (groups.length === 1) return union[0].id;
    return pickVariant(union, overrideId);
  }

  // The treat variant a cart line proves the customer picked ON PURPOSE:
  // it has to be among the offered variants AND must not be the automatic
  // (first) one. Returns null otherwise — a line carrying the automatic
  // size says nothing about intent. See adoptTreatSize() for why this is
  // only ever asked at page load.
  function deliberateTreatVariant(cart, config) {
    const items = (cart && cart.items) || [];
    const line = items.find((item) => isGiftLine(item) && item.properties._ndog_gift === 'guetzli');
    if (!line || !config) return null;
    const sizes = canSizes(items.filter((item) => !isGiftLine(item)));
    const offered = guetzliVariantsFor((config.rules || {}).guetzli, sizes);
    if (offered.length < 2) return null;
    const id = Number(line.variant_id);
    if (Number(offered[0].id) === id) return null;
    return offered.some((variant) => Number(variant.id) === id) ? id : null;
  }

  // The slot a `choice` cart line belongs to (matched via its variant id)
  function slotKeyForLine(config, line) {
    const choices = (config && config.choices) || {};
    const slots = (choices.tier2 || []).concat(choices.tier3 || []);
    const sides = ['a', 'b', 'c', 'd'];
    const hit = slots.find((slot) => sides.some((side) => {
      const variants = (slot[side] && slot[side].variants) || [];
      return variants.some((v) => Number(v.id) === Number(line.variant_id));
    }));
    return hit ? hit.key : null;
  }

  // The gifts the cart SHOULD contain: Map ruleKey → { variantId, quantity }
  function planGifts(cart, config, state) {
    const plan = new Map();
    const items = (cart && cart.items) || [];
    // An upsell line on its way out (see staleUpsellLines) no longer counts
    // as cart value, so the same round already plans the gifts without it
    const stale = staleUpsellLines(cart, config);
    const paid = items.filter((item) => !isGiftLine(item) && !stale.includes(item));
    if (!config || !config.enabled || paid.length === 0) return plan;
    const dismissed = state.dismissed || new Set();
    const overrides = state.variantOverrides || {};
    const rules = config.rules || {};
    const paidCents = paidSubtotalCents(paid);
    const sizes = canSizes(paid);

    // A bundle in the cart switches the VALUE tiers off (chef, 2026-09-03):
    // the bundle already packs treats, lid and Bärchen, so the tier-1 treat
    // and the tier-2/3 choice would gift them twice. Only the bundle's own
    // enclosures (brochure + card) and the subscription bag stay in play.
    const bundleIds = (config.bundleProductIds || []).map(Number);
    const hasBundle = hasBundleLine(paid, config);

    if (!hasBundle && paidCents >= config.tier1Cents) {
      const variantId = pickVariant(guetzliVariantsFor(rules.guetzli, sizes), overrides.guetzli);
      if (variantId && !dismissed.has('guetzli')) plan.set('guetzli', { variantId, quantity: 1 });
    }

    // Display-only bundle mode (setting ndog_gift_bundle_display_only,
    // 2026-09-08): the warehouse packs the bundle from its own parts list,
    // so NO 0-€ lines at all — the drawer box shows static chips instead.
    // Lines already in the cart are reconciled away like any dropped gift.
    if (hasBundle && !config.bundleDisplayOnly) {
      const brochure = pickVariant(sideVariantsFor(rules.brochure, sizes), overrides.brochure);
      const card = pickVariant(sideVariantsFor(rules.card, sizes), overrides.card);
      if (brochure && !dismissed.has('brochure')) plan.set('brochure', { variantId: brochure, quantity: 1 });
      if (card && !dismissed.has('card')) plan.set('card', { variantId: card, quantity: 1 });
      // Bundle contents (Bärli, treats, lid — config.bundleRules, ids fixed
      // in code per store): the size side follows the BUNDLE variant only
      // ("12x400g" → c), never the other cans in the cart.
      const bundleSizes = canSizes(paid.filter((item) => bundleIds.includes(Number(item.product_id))));
      Object.keys(config.bundleRules || {}).forEach((key) => {
        const variantId = pickVariant(sideVariantsFor(config.bundleRules[key], bundleSizes), overrides[key]);
        if (variantId && !dismissed.has(key)) plan.set(key, { variantId, quantity: 1 });
      });
    }

    // Subscription bag: the size side follows the SUBSCRIBED cans only
    // (2026-09-03, Pablo: the bag packs a silicone lid — one bag for
    // 200/400 g, one for 800 g, one for the rest). A one-off 800 g can next
    // to a 400 g subscription must not switch the bag, same as the bundle.
    const subscribed = paid.filter(isSubscriptionLine);
    const hasSubscription = subscribed.length > 0;
    const bag = pickVariant(sideVariantsFor(rules.abo_bag, canSizes(subscribed)), overrides.abo_bag);
    if (hasSubscription && !config.bagBlocked && bag && !dismissed.has('abo_bag')) {
      plan.set('abo_bag', { variantId: bag, quantity: 1 });
    }

    const choice = hasBundle ? null : choiceVariantId(config, state.choiceKey, paidCents, sizes, overrides.choice);
    if (choice) plan.set('choice', { variantId: choice, quantity: 1 });
    return plan;
  }

  // What has to change: { updates: { lineKey: qty }, adds: [{ id, quantity, properties }] }
  function diffGifts(cart, plan) {
    const updates = {};
    const adds = [];
    const seen = new Set();
    ((cart && cart.items) || []).filter(isGiftLine).forEach((line) => {
      const key = line.properties._ndog_gift;
      const want = plan.get(key);
      const wrong = !want || Number(want.variantId) !== Number(line.variant_id) || seen.has(key);
      if (wrong) {
        updates[line.key] = 0;
        return;
      }
      seen.add(key);
      if (line.quantity !== want.quantity) updates[line.key] = want.quantity;
    });
    plan.forEach((want, key) => {
      if (!seen.has(key)) adds.push({ id: want.variantId, quantity: want.quantity, properties: { _ndog_gift: key } });
    });
    return { updates, adds };
  }

  if (customElements.get('ndog-free-gift')) return;

  customElements.define(
    'ndog-free-gift',
    class NdogFreeGift extends HTMLElement {
      constructor() {
        super();
        this.reconciling = false;
        this.tasks = Promise.resolve(); // the one queue for actions + cart events (see run)
        this.taskActive = false;
        this.blockedRules = new Set();
        this.blockedStamps = new Set(); // bundle line keys whose contents stamp failed (no retry loop)
        this.busyCard = null; // card that shows the progress while its own click is running
        this.onCartUpdated = this.onCartUpdated.bind(this);
        this.onGiftRemoved = this.onGiftRemoved.bind(this);
        this.onDrawerClick = this.onDrawerClick.bind(this);
        this.onGiftVariantChange = this.onGiftVariantChange.bind(this);
      }

      // Reads config + session state, hooks into the drawer, runs the initial sync
      connectedCallback() {
        this.config = this.readConfig();
        this.drawer = this.closest('ndog-cart-drawer');
        if (!this.config || !this.drawer) return;
        this.state = this.readState();
        this.drawer.addEventListener('ndog:cart:updated', this.onCartUpdated);
        this.drawer.addEventListener('ndog:gift:removed', this.onGiftRemoved);
        this.drawer.addEventListener('click', this.onDrawerClick);
        this.drawer.addEventListener('change', this.onGiftVariantChange);
        this.run(() => this.initialSync());
      }

      // Runs one action after the other. A tile clicked while a previous
      // action still waits for the server runs AFTER that answer, so it
      // always reads the current cart (two quick picks would otherwise both
      // land in the cart). Every action ends with a settle pass; errors
      // never block the queue.
      run(task) {
        const step = async () => {
          this.taskActive = true;
          try {
            return await task();
          } finally {
            await this.settle();
            this.taskActive = false;
          }
        };
        const next = this.tasks.then(step, step);
        this.tasks = next.catch(() => {});
        return next;
      }

      // Control pass with the drawer's latest cart: catches changes that
      // arrived while an action was running (quantity stepper next to a busy
      // card) — costs no request when everything is in sync
      async settle() {
        try {
          const cart = this.drawer.getCart();
          if (cart) await this.reconcile(cart);
        } catch (error) {
          console.warn('[ndog-free-gift] settle failed', error);
        }
      }

      // The card of a stable control selector — looked up fresh, because a
      // render may have replaced the clicked node while the action waited
      findCard(selector) {
        const control = this.drawer.querySelector(selector);
        return control ? control.closest('[data-gift-tile], [data-upsell-tile]') : null;
      }

      // Unhooks from the drawer
      disconnectedCallback() {
        if (!this.drawer) return;
        this.drawer.removeEventListener('ndog:cart:updated', this.onCartUpdated);
        this.drawer.removeEventListener('ndog:gift:removed', this.onGiftRemoved);
        this.drawer.removeEventListener('click', this.onDrawerClick);
        this.drawer.removeEventListener('change', this.onGiftVariantChange);
      }

      // Config JSON from the Liquid-rendered script tag (null when broken → module stays inactive)
      readConfig() {
        const script = this.querySelector('[data-config]');
        try {
          const config = JSON.parse(script ? script.textContent : 'null');
          return config && config.enabled ? config : null;
        } catch (error) {
          console.warn('[ndog-free-gift] invalid config', error);
          return null;
        }
      }

      // Contents text per bundle line key, rendered by the bundle-contents snippet
      // (display-only mode); {} when absent or broken
      readBundleContentsMap() {
        const script = this.drawer.querySelector('[data-bundle-contents-map]');
        if (!script) return {};
        try {
          return JSON.parse(script.textContent) || {};
        } catch (error) {
          console.warn('[ndog-free-gift] invalid bundle contents map', error);
          return {};
        }
      }

      // Session state: dismissed automatic gifts + the customer's tier choice
      readState() {
        let stored = null;
        try {
          stored = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || 'null');
        } catch (error) {
          stored = null;
        }
        return {
          dismissed: new Set((stored && stored.dismissed) || []),
          choiceKey: (stored && stored.choiceKey) || null,
          variantOverrides: (stored && stored.variantOverrides) || {},
        };
      }

      // Persists the session state
      saveState() {
        try {
          window.sessionStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({
              dismissed: Array.from(this.state.dismissed),
              choiceKey: this.state.choiceKey,
              variantOverrides: this.state.variantOverrides,
            })
          );
        } catch (error) {
          /* storage blocked — state lives in memory only */
        }
      }

      // Page load with a non-empty cart (legacy add flows): fetch the cart once and
      // reconcile. Reads the RAW count: a gifts-only cart renders as empty
      // (data-item-count 0) but its stale gift lines still have to go.
      async initialSync() {
        const items = this.drawer.querySelector('[data-cart-items]');
        const count = items ? parseInt(items.dataset.rawItemCount || items.dataset.itemCount, 10) || 0 : 0;
        if (count === 0) {
          this.resetState();
          return;
        }
        try {
          const cart = await this.drawer.ensureCart();
          this.adoptTreatSize(cart);
          this.syncChoiceFromCart(cart);
          await this.reconcile(cart);
          // A placeholder the sync did not fill (blocked gift) must not spin on
          this.clearPlaceholders([]);
        } catch (error) {
          console.warn('[ndog-free-gift] initial sync failed', error);
        }
      }

      // Page load: take a deliberately picked treat size out of the CART.
      // sessionStorage is per tab, so a customer returning in a new tab (or
      // after closing the browser) would otherwise have their pick silently
      // reset to the automatic size — while the cart itself survives for
      // days (Pablo, 2026-08-23). Trustworthy HERE because the cart has not
      // changed since it was last reconciled, so a line that differs from
      // the automatic variant can only come from a pick. Deliberately NOT
      // called on later cart updates: a line is legitimately stale there
      // (100 g cart with Klein, customer adds an 800 g can) and adopting it
      // would freeze the automatic size rule.
      adoptTreatSize(cart) {
        if (this.state.variantOverrides.guetzli) return;
        const variantId = deliberateTreatVariant(cart, this.config);
        if (!variantId) return;
        this.state.variantOverrides.guetzli = variantId;
        this.saveState();
      }

      // Drawer changed the cart (add / qty / remove) → re-plan, but only after
      // GIFT_SYNC_DELAY_MS (see there); whatever the sync adds pops in. Events
      // fired by our own running action are skipped: that action settles the
      // cart itself when it is done.
      onCartUpdated(event) {
        if (this.taskActive) return;
        const cart = event.detail && event.detail.cart;
        if (!cart) return;
        this.run(async () => {
          const fresh = this.drawer.getCart() || cart;
          this.syncChoiceFromCart(fresh);
          const { adds, pending } = this.planChanges(fresh);
          // Placeholders of gifts that will NOT come go away at once
          this.clearPlaceholders(adds);
          if (!pending) return;
          // Removals and stamps run immediately — an emptied cart drops its
          // gifts and the upsell right away (Pablo, 2026-10-24). Only ADDS
          // wait, see GIFT_SYNC_DELAY_MS.
          if (adds.length === 0) {
            await this.reconcile(fresh);
            return;
          }
          // The Liquid placeholders (data-gift-pending) show what is coming;
          // without one the thin progress line covers the pause
          const hasPlaceholder = !!this.drawer.querySelector('[data-gift-pending]');
          if (!hasPlaceholder) this.drawer.setBusy(true);
          // No checkout until the gifts are in (older drawer builds lack the method)
          if (this.drawer.setCheckoutLocked) this.drawer.setCheckoutLocked(true);
          try {
            await new Promise((resolve) => window.setTimeout(resolve, GIFT_SYNC_DELAY_MS));
            const before = this.readAppearKeys();
            await this.reconcile(this.drawer.getCart() || fresh);
            this.clearPlaceholders([]);
            this.flashAppeared(before);
            // The auto-picked treat size shows its check with the same pop as a click
            adds.forEach((item) => this.flashPicked(`[data-gift-size="${item.id}"]`));
          } finally {
            if (!hasPlaceholder) this.drawer.setBusy(false);
            if (this.drawer.setCheckoutLocked) this.drawer.setCheckoutLocked(false);
          }
        });
      }

      // Drops the Liquid placeholders (data-gift-pending) of gifts that are
      // not about to be added — dismissed or blocked rules, a sync that
      // changed nothing — so no tile spins forever. adds = the planned gift
      // items; their rules keep their placeholder.
      clearPlaceholders(adds) {
        const keep = new Set(adds.map((item) => item.properties && item.properties._ndog_gift));
        this.drawer.querySelectorAll('[data-gift-pending]').forEach((el) => {
          if (!keep.has(el.dataset.giftPending)) el.remove();
        });
      }

      // Identity of an appear candidate across re-renders: the box, a tile
      // group (size vs. choice) or an element bound to a gift key
      appearKey(el) {
        if (el.dataset.giftKey) return `${el.classList[0]}:${el.dataset.giftKey}`;
        if (el.classList.contains('ndog-cart-drawer__gift-group')) return el.querySelector('[data-gift-size]') ? 'group:size' : 'group:choice';
        return 'box';
      }

      // Keys of every appear candidate currently in the drawer
      readAppearKeys() {
        return new Set(Array.from(this.drawer.querySelectorAll(APPEAR_SELECTOR), (el) => this.appearKey(el)));
      }

      // Lets everything the sync just added pop in (box, tile group, chips):
      // only fresh nodes whose key was not there before, and a new parent
      // animates alone — its children would otherwise pop twice. The class
      // drops again on animationend, like .is-just-picked.
      flashAppeared(before) {
        const fresh = Array.from(this.drawer.querySelectorAll(APPEAR_SELECTOR)).filter((el) => !before.has(this.appearKey(el)));
        fresh
          .filter((el) => !fresh.some((other) => other !== el && other.contains(el)))
          .forEach((el) => {
            el.classList.add('is-appearing');
            const done = (event) => {
              if (event.target !== el) return;
              el.classList.remove('is-appearing');
              el.removeEventListener('animationend', done);
            };
            el.addEventListener('animationend', done);
          });
      }

      // Customer removed a gift line: choice → forget the choice, else remember the dismissal
      onGiftRemoved(event) {
        const key = event.detail && event.detail.key;
        if (!key) return;
        if (key === 'choice') this.state.choiceKey = null;
        else this.state.dismissed.add(key);
        delete this.state.variantOverrides[key];
        this.saveState();
      }

      // Gift variant <select>: on a chip (or the chosen tile) it overrides the
      // variant; on a not-yet-chosen tile picking a size CHOOSES that gift
      onGiftVariantChange(event) {
        const select = event.target.closest('[data-gift-variant]');
        if (!select) return;
        const chip = select.closest('[data-gift-key]');
        const tile = select.closest('[data-gift-tile]');
        const button = tile ? tile.querySelector('[data-gift-choice]') : null;
        if (!chip && !button) return;
        const variantId = Number(select.value);
        const slotKey = button ? button.dataset.giftChoice : null;
        const flashSelector = chip ? null : `[data-gift-choice="${slotKey}"]`;
        return this.run(async () => {
          let card = null;
          if (chip) {
            this.state.variantOverrides[chip.dataset.giftKey] = variantId;
          } else {
            this.state.choiceKey = slotKey;
            this.state.variantOverrides.choice = variantId;
            this.paintChoiceTiles();
            this.paintUpsellTiles(null);
            card = this.findCard(flashSelector);
            this.setCardBusy(card);
          }
          this.saveState();
          try {
            let cart = await this.drawer.ensureCart();
            if (!chip) cart = (await this.removeUpsellLine()) || cart;
            await this.reconcile(cart);
          } catch (error) {
            console.warn('[ndog-free-gift] variant change failed', error);
          } finally {
            this.clearCardBusy(card);
            this.flashPicked(flashSelector);
          }
        });
      }

      // Tile click: pick (or un-pick) the tier gift and reconcile. Picking a
      // free gift drops the upsell line — one shared choice slot (Pablo).
      // The toggle is decided INSIDE the queue, against the state a previous
      // action left behind — not against what the tile showed on click.
      onDrawerClick(event) {
        const upsell = event.target.closest('[data-upsell-add]');
        if (upsell) return this.onUpsellClick(upsell);
        const sizeTile = event.target.closest('[data-gift-size]');
        if (sizeTile) return this.onGiftSizeClick(sizeTile);
        const tile = event.target.closest('[data-gift-choice]');
        if (!tile) return;
        const key = tile.dataset.giftChoice;
        const selector = `[data-gift-choice="${key}"]`;
        return this.run(async () => {
          this.state.choiceKey = this.state.choiceKey === key ? null : key;
          delete this.state.variantOverrides.choice; // a new slot starts with its automatic variant
          this.saveState();
          this.paintChoiceTiles();
          this.paintUpsellTiles(null);
          const card = this.findCard(selector);
          this.setCardBusy(card);
          try {
            let cart = await this.drawer.ensureCart();
            if (this.state.choiceKey) cart = (await this.removeUpsellLine()) || cart;
            await this.reconcile(cart);
          } catch (error) {
            console.warn('[ndog-free-gift] choice failed', error);
          } finally {
            this.clearCardBusy(card);
            this.flashPicked(selector);
          }
        });
      }

      // Treat size tile (tier-1 gift, chef 2026-08-23): radio choice — only
      // the VARIANT changes, the treat itself stays, so there is no
      // un-picking. Same pathway the chip select used before.
      // "Already pressed" is only trusted while nothing runs: during an
      // action the tile shows the OPTIMISTIC paint of an earlier click, so a
      // third quick click back to that size would otherwise be swallowed.
      onGiftSizeClick(button) {
        const variantId = Number(button.dataset.giftSize);
        if (!variantId) return;
        if (!this.taskActive && button.getAttribute('aria-pressed') === 'true') return;
        const selector = `[data-gift-size="${variantId}"]`;
        return this.run(async () => {
          this.state.variantOverrides.guetzli = variantId;
          this.saveState();
          this.paintSizeTiles(variantId);
          const card = this.findCard(selector);
          this.setCardBusy(card);
          try {
            const cart = await this.drawer.ensureCart();
            await this.reconcile(cart);
          } catch (error) {
            console.warn('[ndog-free-gift] treat size failed', error);
          } finally {
            this.clearCardBusy(card);
            this.flashPicked(selector);
          }
        });
      }

      // Optimistic highlight for the treat sizes (exactly one active). Since
      // 2026-08-26 every size is its own card, so — like paintChoiceTiles —
      // the surrounding [data-gift-tile] carries the highlight while the
      // button carries aria-pressed.
      paintSizeTiles(variantId) {
        this.drawer.querySelectorAll('[data-gift-size]').forEach((el) => {
          const selected = Number(el.dataset.giftSize) === Number(variantId);
          el.setAttribute('aria-pressed', String(selected));
          const card = el.closest('[data-gift-tile]');
          if (card) card.classList.toggle('is-selected', selected);
        });
      }

      // The upsell shares the single choice slot with the free gifts:
      // silently removes the upsell cart line (returns the fresh cart) or
      // null when none is in the cart. Since every upsell variant has its own
      // tile (2026-08-26), only the tile of the variant IN the cart carries a
      // line key — the selector has to ask for that key, not for the first
      // upsell tile it finds.
      async removeUpsellLine() {
        const card = this.drawer.querySelector('[data-upsell-tile][data-upsell-line-key]');
        const key = card ? card.dataset.upsellLineKey : null;
        if (!key) return null;
        return this.drawer.updateLines({ [key]: 0 }, true);
      }

      // Upsell tile click: toggle the discounted product as a PAID line
      // (no _ndog_gift property — planGifts/diffGifts never touch it).
      // Selecting it takes the single choice slot: a chosen free gift is
      // dropped first (Pablo, 2026-08-21 — either free gift OR upsell).
      // Whether the tile is currently IN the cart is read inside the queue:
      // a previous action may have re-rendered the row in the meantime.
      onUpsellClick(button) {
        const variantId = Number(button.dataset.upsellAdd);
        if (!variantId) return;
        const selector = `[data-upsell-add="${variantId}"]`;
        return this.run(async () => {
          const card = this.findCard(selector);
          if (!card) return;
          this.setCardBusy(card);
          try {
            if (card.dataset.upsellLineKey) {
              this.paintUpsellTiles(null);
              await this.drawer.updateLines({ [card.dataset.upsellLineKey]: 0 });
            } else {
              this.state.choiceKey = null;
              delete this.state.variantOverrides.choice;
              this.saveState();
              this.paintChoiceTiles();
              this.paintUpsellTiles(variantId);
              // Another upsell size may hold the shared slot — it goes first,
              // otherwise both sizes would end up in the cart (2026-08-26).
              await this.removeUpsellLine();
              const cart = await this.drawer.ensureCart();
              const choiceLine = ((cart && cart.items) || []).find((item) => isGiftLine(item) && item.properties._ndog_gift === 'choice');
              if (choiceLine) await this.drawer.updateLines({ [choiceLine.key]: 0 }, true);
              // `_ndog_upsell` marks the line as "picked in the drawer" (Pablo,
              // 2026-08-26): the drawer then keeps it OUT of the item list — the
              // marked tile is its representation, exactly like a chosen free
              // gift. Underscore = hidden from checkout/order display. NOT
              // `_ndog_gift`: the reconciler would treat it as an unplanned gift
              // and delete it, and it is a PAID line.
              await this.drawer.addItems([{ id: variantId, quantity: 1, properties: { _ndog_upsell: '1' } }]);
            }
          } catch (error) {
            console.warn('[ndog-free-gift] upsell toggle failed', error);
            await this.drawer.refresh();
          } finally {
            this.clearCardBusy(card);
            this.flashPicked(selector);
          }
        });
      }


      // Optimistic highlight: aria-pressed + card style follow the chosen slot
      paintChoiceTiles() {
        this.drawer.querySelectorAll('[data-gift-choice]').forEach((el) => {
          const selected = el.dataset.giftChoice === this.state.choiceKey;
          el.setAttribute('aria-pressed', String(selected));
          const card = el.closest('[data-gift-tile]');
          if (card) card.classList.toggle('is-selected', selected);
        });
      }

      // Progress feedback on the CLICKED card instead of the thin line at the
      // top of the drawer (Pablo, 2026-08-26): the card blocks itself and
      // spins until the cart round trip is done, and its check badge stays
      // hidden meanwhile (CSS) — so the check really means "saved".
      // Everything else (quantity, removals, automatic gift syncs) keeps the
      // top line: there is no card to point at.
      setCardBusy(card) {
        this.busyCard = card || null;
        if (card) card.classList.add('is-busy');
      }

      // On the painting path the re-render has already replaced the node, so
      // this only matters when nothing was rendered (no-op or error).
      // Takes the card it belongs to: with two clicks in a row the first
      // handler must not release the second one's card.
      clearCardBusy(card) {
        if (!card) return;
        card.classList.remove('is-busy');
        if (this.busyCard === card) this.busyCard = null;
      }

      // Lets the check badge pop in on the card the customer just picked. The
      // re-render has replaced the node by now, so the fresh one is looked up
      // through the STABLE data attribute of the clicked control (variant id
      // or slot key). Only that one card animates — a class on every render
      // would make all chosen gifts twitch on any cart change.
      flashPicked(selector) {
        if (!selector) return;
        const control = this.drawer.querySelector(selector);
        const card = control ? control.closest('[data-gift-tile], [data-upsell-tile]') : null;
        if (!card || !card.classList.contains('is-selected')) return;
        card.classList.add('is-just-picked');
        card.addEventListener('animationend', () => card.classList.remove('is-just-picked'), { once: true });
      }

      // Optimistic highlight for the upsell tiles — at most one active, null
      // clears them all (the free gifts and the upsell share ONE slot, so
      // picking on one side has to un-mark the other immediately).
      paintUpsellTiles(variantId) {
        this.drawer.querySelectorAll('[data-upsell-add]').forEach((el) => {
          const selected = variantId != null && Number(el.dataset.upsellAdd) === Number(variantId);
          el.setAttribute('aria-pressed', String(selected));
          const card = el.closest('[data-upsell-tile]');
          if (card) card.classList.toggle('is-selected', selected);
        });
      }

      // The cart is the truth for the choice: an existing `choice` line wins, no line = no choice
      syncChoiceFromCart(cart) {
        const line = ((cart && cart.items) || []).find((item) => isGiftLine(item) && item.properties._ndog_gift === 'choice');
        this.state.choiceKey = line ? slotKeyForLine(this.config, line) : null;
        this.saveState();
        if (cart && cart.item_count === 0) this.resetState();
      }

      // Empty cart → forget dismissals, choice and variant overrides
      resetState() {
        this.state = { dismissed: new Set(), choiceKey: null, variantOverrides: {} };
        this.saveState();
      }

      // What the cart still needs to match the plan (pure, no request):
      // { updates, adds, stamps, pending } — pending = false means in sync
      planChanges(cart) {
        const plan = planGifts(cart, this.config, this.state);
        this.blockedRules.forEach((key) => plan.delete(key));
        const { updates, adds } = diffGifts(cart, plan);
        // The upsell leaves with its row (below tier 2 / bundle) — same
        // round as the gifts it no longer counts towards
        staleUpsellLines(cart, this.config).forEach((upsellLine) => {
          updates[upsellLine.key] = 0;
        });
        const stamps = bundleContentStamps(cart, this.config, this.readBundleContentsMap()).filter(
          (stamp) => !this.blockedStamps.has(stamp.id)
        );
        const pending = Object.keys(updates).length > 0 || adds.length > 0 || stamps.length > 0;
        return { updates, adds, stamps, pending };
      }

      // Plans, diffs and applies gift changes; then one control round that must be a no-op
      async reconcile(cart, round = 0) {
        if (this.reconciling) return;
        const { updates, adds, stamps, pending } = this.planChanges(cart);
        if (!pending) return;
        const hasUpdates = Object.keys(updates).length > 0;
        if (round >= 2) {
          console.warn('[ndog-free-gift] still out of sync after 2 rounds — stopping', updates, adds);
          return;
        }
        this.reconciling = true;
        // The clicked card already shows the progress — two indicators for one
        // operation would be noise (Pablo, 2026-08-26).
        if (!this.busyCard) this.drawer.setBusy(true);
        let latest = cart;
        try {
          // When adds follow, the update runs silent (no re-render) — only the
          // final operation paints, so a variant swap never makes boxes jump.
          latest = await this.stampBundleContents(stamps, latest, !hasUpdates && adds.length === 0);
          if (hasUpdates) latest = await this.drawer.updateLines(updates, adds.length > 0);
          if (adds.length > 0) latest = await this.addSafely(adds, latest);
        } catch (error) {
          console.warn('[ndog-free-gift] sync failed', error);
          await this.drawer.refresh();
        } finally {
          this.reconciling = false;
          if (!this.busyCard) this.drawer.setBusy(false);
        }
        this.syncChoiceFromCart(latest);
        await this.reconcile(latest, round + 1);
      }

      // Writes the contents text onto each bundle line (one change call per line;
      // only the very last operation of the round paints). A failed stamp is
      // blocked for this session — the cart works without it, the mail just
      // lacks the contents list.
      async stampBundleContents(stamps, latest, paintsLast) {
        let cart = latest;
        let failed = false;
        for (let index = 0; index < stamps.length; index += 1) {
          const isLast = paintsLast && index === stamps.length - 1;
          try {
            cart = await this.drawer.updateLineProperties(stamps[index], !isLast);
          } catch (error) {
            failed = true;
            this.blockedStamps.add(stamps[index].id);
            console.warn('[ndog-free-gift] could not stamp bundle contents', error.message);
          }
        }
        if (failed && paintsLast) await this.drawer.refresh();
        return cart;
      }

      // Batch add; on failure (or same variant for two rules — Shopify merges those) one by one.
      // A rule that cannot be added is blocked for this session.
      async addSafely(adds, latest) {
        const variantIds = adds.map((item) => item.id);
        const hasDuplicateVariant = new Set(variantIds).size !== variantIds.length;
        if (!hasDuplicateVariant) {
          try {
            return await this.drawer.addItems(adds);
          } catch (batchError) {
            /* fall through to one-by-one */
          }
        }
        let cart = latest;
        let failed = false;
        for (const item of adds) {
          try {
            cart = await this.drawer.addItems([item]);
          } catch (error) {
            failed = true;
            this.blockedRules.add(item.properties._ndog_gift);
            console.warn('[ndog-free-gift] could not add gift', item.properties._ndog_gift, error.message);
          }
        }
        // A failed add after a silent update would leave the DOM stale — resync once
        if (failed) await this.drawer.refresh();
        return cart;
      }
    }
  );
})();
